'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type ForwardedRef,
} from 'react';

import { useWorkbenchConfig } from '@/components/Contexts/WorkbenchConfigContext';
import { buildPreviewFrameUrl } from '@/lib/previewFrameUrl';
import {
  EditorType,
  type BridgeInboundMessage,
  type BridgeOutboundMessage,
  type BridgePinData,
  type CommentUserInfo,
  type DevicePreset,
  type Pin,
} from '@/types/feedback';
import { getAvatarColor, getAvatarInitial } from '@/utils/avatarColor';

import { PreviewFrameLoader } from './PreviewFrameLoader';

interface ProxyFrameProps {
  targetUrl: string;
  deviceWidth: number;
  device?: DevicePreset;
  fullscreen?: boolean;
  pins: Pin[];
  commenterInfoMap?: Record<string, CommentUserInfo>;
  pinMode: boolean;
  inspectOnly?: boolean;
  pinsVisible: boolean;
  scrollLocked?: boolean;
  selectionDisabled?: boolean;
  onReady?: () => void;
  onLoading?: () => void;
  showLoadingOverlay?: boolean;
  /** Hide iframe paint until the bridge reports ready (avoids content flashing under the loader). */
  contentVisible?: boolean;
  onPinAdded: (
    pin: Omit<Pin, 'label' | 'createdAt'>,
    viewportX: number,
    viewportY: number,
  ) => void;
  onPinMarkerClick: (
    pinId: string,
    viewportX: number,
    viewportY: number,
  ) => void;
  onPinMoved: (
    pinId: string,
    selector: string,
    offsetXPct: number,
    offsetYPct: number,
    fallbackXPct: number,
    fallbackYPct: number,
  ) => void;
  onScrolledToPin: (
    pinId: string,
    viewportX: number,
    viewportY: number,
  ) => void;
  onPageChanged?: (pagePath: string) => void;
  onScroll?: (scrollY: number) => void;
  /** The site was clicked while a popover was open; treat it as a blur. */
  onDismissPopover?: () => void;
}

export interface ProxyFrameHandle {
  sendMessage: (msg: BridgeOutboundMessage) => void;
  scrollToPin: (pinId: string) => void;
  syncScroll: (scrollY: number) => void;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function getPinAvatarName(
  pin: Pin,
  commenterInfoMap: Record<string, CommentUserInfo>,
): string | undefined {
  return pin.commenterId
    ? commenterInfoMap[pin.commenterId]?.userName
    : undefined;
}

export function toBridgePins(
  pins: Pin[],
  commenterInfoMap: Record<string, CommentUserInfo> = {},
): BridgePinData[] {
  return pins.map((p) => {
    const avatarName = getPinAvatarName(p, commenterInfoMap);
    return {
      id: p.id,
      selector: p.selector,
      offsetXPct: p.offsetXPct,
      offsetYPct: p.offsetYPct,
      fallbackXPct: p.fallbackXPct,
      fallbackYPct: p.fallbackYPct,
      label: getAvatarInitial(avatarName),
      avatarColor: getAvatarColor(avatarName),
      status: p.status,
      isLegacy: p.isLegacy,
      legacyX: p.legacyX,
      legacyY: p.legacyY,
    };
  });
}

const isDev = process.env.NODE_ENV === 'development';
const appBasePath = (process.env.domain ?? '').replace(/\/$/, '');

function ProxyFrameInner(
  {
    targetUrl,
    deviceWidth,
    device = 'desktop',
    fullscreen = false,
    pins,
    commenterInfoMap,
    pinMode,
    inspectOnly = false,
    pinsVisible,
    scrollLocked = false,
    selectionDisabled = false,
    onReady,
    onLoading,
    showLoadingOverlay = false,
    contentVisible = true,
    onPinAdded,
    onPinMarkerClick,
    onPinMoved,
    onScrolledToPin,
    onPageChanged,
    onScroll,
    onDismissPopover,
  }: ProxyFrameProps,
  ref: ForwardedRef<ProxyFrameHandle>,
) {
  const { editorType } = useWorkbenchConfig();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendMessage = useCallback((msg: BridgeOutboundMessage) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*');
  }, []);

  const scrollToPin = useCallback(
    (pinId: string) => {
      sendMessage({ type: 'FB_SCROLL_TO_PIN', payload: { pinId } });
    },
    [sendMessage],
  );

  const syncScroll = useCallback(
    (scrollY: number) => {
      sendMessage({ type: 'FB_SYNC_SCROLL', payload: { scrollY } });
    },
    [sendMessage],
  );

  useImperativeHandle(ref, () => ({ sendMessage, scrollToPin, syncScroll }), [
    sendMessage,
    scrollToPin,
    syncScroll,
  ]);

  useEffect(() => {
    sendMessage({ type: 'FB_SET_MODE', payload: { pinMode, inspectOnly } });
  }, [pinMode, inspectOnly, sendMessage]);

  useEffect(() => {
    sendMessage({
      type: 'FB_SET_PINS',
      payload: { pins: toBridgePins(pins, commenterInfoMap) },
    });
  }, [pins, commenterInfoMap, sendMessage]);

  useEffect(() => {
    sendMessage({
      type: 'FB_SET_PINS_VISIBLE',
      payload: { visible: pinsVisible },
    });
  }, [pinsVisible, sendMessage]);

  useEffect(() => {
    sendMessage({
      type: 'FB_SET_SCROLL_LOCKED',
      payload: { locked: scrollLocked },
    });
  }, [scrollLocked, sendMessage]);

  useEffect(() => {
    sendMessage({
      type: 'FB_SET_SELECTION_DISABLED',
      payload: { disabled: selectionDisabled },
    });
    // Closing the popover is what ends a pin highlight; the bridge keeps the
    // overlay up until it is told the selection is live again.
    if (!selectionDisabled) {
      sendMessage({ type: 'FB_CLEAR_HIGHLIGHT' });
    }
  }, [selectionDisabled, sendMessage]);

  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const msg = e.data as BridgeInboundMessage | unknown;
      if (!msg || typeof msg !== 'object' || !('type' in (msg as object)))
        return;
      const typed = msg as BridgeInboundMessage;

      if (typed.type === 'FB_PIN_CLICK') {
        if (!isRecord(typed.payload)) return;
        const {
          selector,
          offsetXPct,
          offsetYPct,
          clientX,
          clientY,
          pageWidth,
          scrollX: frameScrollX = 0,
          scrollY: frameScrollY = 0,
          pathname,
        } = typed.payload;

        const iframe = iframeRef.current;
        const rect = iframe?.getBoundingClientRect();
        const scale = fullscreen ? 1 : rect ? rect.width / deviceWidth : 1;
        const viewportX = (rect?.left ?? 0) + clientX * scale;
        const viewportY = (rect?.top ?? 0) + clientY * scale;

        onPinAdded(
          {
            id: Date.now().toString(),
            selector,
            offsetXPct,
            offsetYPct,
            fallbackXPct:
              pageWidth > 0 ? (frameScrollX + clientX) / pageWidth : 0,
            fallbackYPct:
              pageWidth > 0 ? (frameScrollY + clientY) / pageWidth : 0,
            pagePath: pathname.replace(/^\//, ''),
          },
          viewportX,
          viewportY,
        );
      }

      if (typed.type === 'FB_PIN_MARKER_CLICK') {
        if (!isRecord(typed.payload)) return;
        const { pinId, x, y } = typed.payload;
        const iframe = iframeRef.current;
        if (!iframe) return;
        // getBoundingClientRect already accounts for CSS transform on the parent
        const rect = iframe.getBoundingClientRect();
        // scale = how much the iframe is visually shrunk
        const scale = fullscreen ? 1 : rect.width / deviceWidth;
        onPinMarkerClick(pinId, rect.left + x * scale, rect.top + y * scale);
      }

      if (typed.type === 'FB_PIN_MOVED') {
        if (inspectOnly) return;
        if (!isRecord(typed.payload)) return;
        const {
          pinId,
          selector,
          offsetXPct,
          offsetYPct,
          fallbackXPct,
          fallbackYPct,
        } = typed.payload;
        onPinMoved(
          pinId,
          selector,
          offsetXPct,
          offsetYPct,
          fallbackXPct,
          fallbackYPct,
        );
      }

      if (typed.type === 'FB_SCROLLED_TO_PIN') {
        if (!isRecord(typed.payload)) return;
        const { pinId, x, y } = typed.payload;
        const iframe = iframeRef.current;
        if (!iframe) return;
        const rect = iframe.getBoundingClientRect();
        const scale = fullscreen ? 1 : rect.width / deviceWidth;
        onScrolledToPin(pinId, rect.left + x * scale, rect.top + y * scale);
      }

      if (typed.type === 'FB_PAGE_CHANGED') {
        if (!isRecord(typed.payload)) return;
        onPageChanged?.(typed.payload.pathname.replace(/^\//, ''));
      }

      if (typed.type === 'FB_SCROLL') {
        if (!isRecord(typed.payload)) return;
        onScroll?.(typed.payload.scrollY);
      }

      if (typed.type === 'FB_DISMISS_POPOVER') {
        onDismissPopover?.();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    onPinAdded,
    onPinMarkerClick,
    onPinMoved,
    onScrolledToPin,
    onPageChanged,
    onScroll,
    onDismissPopover,
    deviceWidth,
    fullscreen,
    inspectOnly,
  ]);

  function sendInitialState() {
    sendMessage({ type: 'FB_SET_MODE', payload: { pinMode, inspectOnly } });
    sendMessage({
      type: 'FB_SET_PINS',
      payload: { pins: toBridgePins(pins, commenterInfoMap) },
    });
    sendMessage({
      type: 'FB_SET_PINS_VISIBLE',
      payload: { visible: pinsVisible },
    });
    sendMessage({
      type: 'FB_SET_SCROLL_LOCKED',
      payload: { locked: scrollLocked },
    });
    sendMessage({
      type: 'FB_SET_SELECTION_DISABLED',
      payload: { disabled: selectionDisabled },
    });
    onReady?.();
  }

  function handleLoad() {
    onLoading?.();
    if (isDev) {
      // Proxy already injected bridge.js into the HTML
      sendInitialState();
      return;
    }
    // Same-origin: inject bridge.js via DOM and defer state until it's ready
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      const script = win.document.createElement('script');
      script.src = `${appBasePath}/bridge.js`;
      script.onload = sendInitialState;
      win.document.head.appendChild(script);
    } catch {
      // Same-origin DOM access blocked (e.g. cross-origin redirect in iframe)
    }
  }

  const iframeSrc = buildPreviewFrameUrl(targetUrl, { editorType, device });
  const iframeKey = editorType === EditorType.Editor ? device : undefined;

  useLayoutEffect(() => {
    onLoading?.();
  }, [iframeSrc, iframeKey, onLoading]);

  return (
    <div className="relative h-full w-full">
      <iframe
        key={iframeKey}
        ref={iframeRef}
        src={iframeSrc}
        onLoad={handleLoad}
        style={{
          width: fullscreen ? '100%' : deviceWidth,
          height: '100%',
          border: 'none',
          display: 'block',
          visibility: contentVisible ? 'visible' : 'hidden',
        }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        title="Proxied site preview"
      />
      {showLoadingOverlay && <PreviewFrameLoader />}
    </div>
  );
}

export const ProxyFrame = forwardRef(ProxyFrameInner);
